import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ChevronLeft, Loader2, Sparkles, ListChecks } from "lucide-react";
import { PeopleSearch } from "./PeopleSearch";
import { V } from "@/lib/vocab";

const STANDING = [["good", "Good standing"], ["delinquent", "Delinquent"], ["violation", "Open violation"], ["legal", "In legal"]];
const chip = (on) => `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-ink bg-ink text-white" : "border-sand-200 bg-white text-ink/70 hover:border-ink"}`;

const Toggle = ({ on, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className={chip(on)}>{children}</button>
);

const GroupForm = ({ tenantId, group, options, onSaved, onBack }) => {
  const [name, setName] = useState(group?.name || "");
  const [kind, setKind] = useState(group?.kind || "smart");
  const [rules, setRules] = useState({ roles: [], streets: [], unit_prefixes: [], standing: [], ...(group?.rules || {}) });
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const flip = (key, v) => setRules((r) => ({ ...r, [key]: r[key].includes(v) ? r[key].filter((x) => x !== v) : [...r[key], v] }));

  useEffect(() => {
    if (group?.kind === "static" && group.person_ids?.length) {
      api.get(`/tenants/${tenantId}/messages/people?ids=${group.person_ids.join(",")}`).then((r) => setPeople(r.data || [])).catch(() => {});
    }
  }, [group, tenantId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, kind, rules, person_ids: people.map((p) => p.id) };
      const r = group?.id ? await api.put(`/tenants/${tenantId}/groups/${group.id}`, body) : await api.post(`/tenants/${tenantId}/groups`, body);
      toast.success(`${r.data.name} — ${r.data.member_count} ${r.data.member_count === 1 ? "person" : "people"}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the group"); }
    setSaving(false);
  };

  return (
    <div data-testid="group-form">
      <button type="button" onClick={onBack} data-testid="group-form-back" className="inline-flex items-center gap-1 text-xs font-semibold text-ink/60 hover:text-ink mb-3"><ChevronLeft className="h-3.5 w-3.5" /> All groups</button>
      <label className="block text-xs font-bold text-ink/50 mb-1">Name</label>
      <input data-testid="group-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Owners on Elm Street"
        className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 mb-4" />
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button type="button" data-testid="group-kind-smart" onClick={() => setKind("smart")} className={`text-left rounded-2xl border p-3 ${kind === "smart" ? "border-ink bg-sand-50" : "border-sand-200"}`}>
          <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><Sparkles className="h-4 w-4" /> Smart rules</div>
          <p className="text-[11px] text-ink/55 mt-0.5">Follows the roster — new matches join automatically.</p>
        </button>
        <button type="button" data-testid="group-kind-static" onClick={() => setKind("static")} className={`text-left rounded-2xl border p-3 ${kind === "static" ? "border-ink bg-sand-50" : "border-sand-200"}`}>
          <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><ListChecks className="h-4 w-4" /> Hand-picked list</div>
          <p className="text-[11px] text-ink/55 mt-0.5">You choose exactly who's on it.</p>
        </button>
      </div>

      {kind === "smart" ? (
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold text-ink/50 mb-1.5">Who</div>
            <div className="flex flex-wrap gap-1.5">{(options.roles || []).map((r) => <Toggle key={r.value} testId={`rule-role-${r.value}`} on={rules.roles.includes(r.value)} onClick={() => flip("roles", r.value)}>{r.label}</Toggle>)}</div>
          </div>
          {options.streets?.length > 0 && (
            <div>
              <div className="text-xs font-bold text-ink/50 mb-1.5">Street</div>
              <div className="flex flex-wrap gap-1.5">{options.streets.map((s) => <Toggle key={s.value} testId={`rule-street-${s.value.replace(/\W+/g, "-")}`} on={rules.streets.includes(s.value)} onClick={() => flip("streets", s.value)}>{s.value} <span className="opacity-60">{s.count}</span></Toggle>)}</div>
            </div>
          )}
          {options.unit_prefixes?.length > 0 && (
            <div>
              <div className="text-xs font-bold text-ink/50 mb-1.5">Building / unit prefix</div>
              <div className="flex flex-wrap gap-1.5">{options.unit_prefixes.map((s) => <Toggle key={s.value} testId={`rule-prefix-${s.value}`} on={rules.unit_prefixes.includes(s.value)} onClick={() => flip("unit_prefixes", s.value)}>{s.value.toUpperCase()}… <span className="opacity-60">{s.count}</span></Toggle>)}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-bold text-ink/50 mb-1.5">Account standing</div>
            <div className="flex flex-wrap gap-1.5">{STANDING.map(([v, l]) => <Toggle key={v} testId={`rule-standing-${v}`} on={rules.standing.includes(v)} onClick={() => flip("standing", v)}>{l}</Toggle>)}</div>
          </div>
          <p className="text-[11px] text-ink/45">{V.many("person")} must match every family you set (any within a family).</p>
        </div>
      ) : (
        <PeopleSearch tenantId={tenantId} selected={people} onChange={setPeople} max={500} testId="group-people" />
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onBack} className="rounded-full bg-white border border-sand-200 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-50">Cancel</button>
        <button type="button" data-testid="group-save" onClick={save} disabled={saving || !name.trim()}
          className="rounded-full bg-ink text-white font-semibold px-5 py-2 text-sm hover:bg-ink-700 disabled:opacity-50 inline-flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {group?.id ? "Save group" : "Create group"}
        </button>
      </div>
    </div>
  );
};

export const GroupsDialog = ({ open, onOpenChange, tenantId, groups, reload }) => {
  const [editing, setEditing] = useState(null);
  const [options, setOptions] = useState({});
  useEffect(() => {
    if (!open || !tenantId) return;
    setEditing(null);
    api.get(`/tenants/${tenantId}/groups-options`).then((r) => setOptions(r.data || {})).catch(() => {});
  }, [open, tenantId]);

  const remove = async (g) => {
    if (!window.confirm(`Delete the group "${g.name}"? Scheduled messages to it will fail.`)) return;
    try { await api.delete(`/tenants/${tenantId}/groups/${g.id}`); toast.success("Group deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };
  const describe = (g) => {
    if (g.kind === "static") return `Hand-picked · ${g.person_ids?.length || 0} chosen`;
    const r = g.rules || {};
    return ["Smart", r.roles?.length && r.roles.join("/"), r.streets?.length && r.streets.join(", "), r.unit_prefixes?.length && `units ${r.unit_prefixes.join(", ")}…`, r.standing?.length && r.standing.join("/")].filter(Boolean).join(" · ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="groups-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{V.one("person")} groups</DialogTitle>
          <DialogDescription className="text-xs">Reusable audiences for messages — by role, street, building or a list you pick.</DialogDescription>
        </DialogHeader>
        {editing ? (
          <GroupForm tenantId={tenantId} group={editing.id ? editing : null} options={options} onBack={() => setEditing(null)}
            onSaved={() => { setEditing(null); reload(); }} />
        ) : (
          <div data-testid="groups-list">
            {groups.length === 0 && <p className="text-sm text-ink/50 py-4 text-center">No groups yet.</p>}
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.id} data-testid={`group-row-${g.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-sand-200 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-ink truncate">{g.name} <span className="text-ink/40 font-normal">· {g.member_count}</span></div>
                    <div className="text-[11px] text-ink/50 truncate">{describe(g)}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`group-edit-${g.id}`} onClick={() => setEditing(g)} className="rounded-full p-2 text-ink/50 hover:bg-sand-100 hover:text-ink" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button type="button" data-testid={`group-delete-${g.id}`} onClick={() => remove(g)} className="rounded-full p-2 text-ink/50 hover:bg-redline-50 hover:text-redline-700" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" data-testid="group-new" onClick={() => setEditing({})}
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-ink text-white font-semibold px-4 py-2.5 text-sm hover:bg-ink-700"><Plus className="h-4 w-4" /> New group</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
